import { useSearchBox } from "react-instantsearch"
import { FormEvent, forwardRef, KeyboardEvent, useEffect, useState } from "react";
import useDebounce from "../utils/debounce";
import SearchIcon from "./SearchIcon";
import { AutoCompleteProvider } from "./AutoCompleteProvider";
// export default function

type Props = { onReset?: () => void, onSearch?: () => void };

const SearchBox = forwardRef<HTMLInputElement, Props>(({ onReset = () => {}, onSearch = () => {} }, ref) => {
  const { query, refine } = useSearchBox();
  const [searchTerm, setSearchTerm] = useState(query)
  const [showAutoComplete, setShowAutoComplete] = useState(false)

  const debounceTimeInMs = 250;
  const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeInMs)

  useEffect(() => {
    if (debouncedSearchTerm) {
        refine(searchTerm)
    } else {
        refine('')
    }
}, [debouncedSearchTerm])
  
  const gotoSearchPage = () => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('query', searchTerm);

    const baseUrl = window.location.origin;
    window.location.href = `${baseUrl}/search?${currentParams.toString()}`;
  }

  const submitHandler = (event: FormEvent) => {
    event.preventDefault();
    gotoSearchPage();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>, suggestion: string) => {
    if (event.key === 'Tab' && suggestion) {
      event.preventDefault();
      setSearchTerm(suggestion);
      refine(suggestion);
    } else if (event.key === 'ArrowRight' && suggestion) {
      // Accept auto-completion with right arrow at end of input
      const input = event.currentTarget;
      if (input.selectionStart === input.value.length) {
        event.preventDefault();
        setSearchTerm(suggestion);
        refine(suggestion);
      }
    } else if (event.key === 'Enter') {
      gotoSearchPage();
    }
  }

  const resetHandler = (event: FormEvent) => {
    event.preventDefault();

    refine('');
    onReset();
  };

  const handleSearch = (value: any) => {
    onSearch();
    setSearchTerm(value);
    setShowAutoComplete(!!value);

    if (!value || value === "" ) {
      refine('');
      setSearchTerm('');
      setShowAutoComplete(false);
      onReset();
    }
  }

  return (
    <AutoCompleteProvider query={searchTerm}>
      {(suggestion) => (
        <form
          action={`/search`}
          onSubmit={submitHandler}
          onReset={resetHandler}
        >
          <div className="tw-hidden">
            <button onClick={resetHandler}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4"><path d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
            </button>
          </div>

          {/* DESKTOP SEARCH INPUT */}
          <div className="tw-relative">
            {/* Auto-completion overlay */}
            {suggestion && showAutoComplete && (
              <div className="tw-absolute tw-left-0 tw-top-0 tw-w-full tw-py-3 tw-px-0 tw-pointer-events-none tw-text-gray-400 tw-z-0">
                <span className="tw-invisible">{searchTerm}</span>
                <span>{suggestion.slice(searchTerm.length)}</span>
              </div>
            )}
            
            <input
              ref={ref}
              autoFocus={true}
              spellCheck={false}
              value={searchTerm}
              onChange={(e) => { handleSearch(e.target.value) }}
              onKeyDown={(e) => onKeyDown(e, suggestion)}
              onFocus={() => setShowAutoComplete(!!searchTerm)}
              onBlur={() => setShowAutoComplete(false)}
              placeholder="What are you looking for?"
              className="tw-relative tw-z-10 tw-w-full tw-py-3 tw-px-0 tw-border-b-[#ddd] tw-border-0 tw-border-b tw-border-solid tw-outline-none tw-bg-transparent tw-rounded-none"
              type="text"
            />

            <button className="tw-absolute tw--translate-y-2/4 tw-top-2/4 tw-right-0 tw-border-0 tw-z-20 tw-bg-white" type="submit">
              <span className="tw-sr-only">Search</span>
              <SearchIcon />
            </button>
          </div>
        </form>
      )}
    </AutoCompleteProvider>
  )
});

SearchBox.displayName = 'SearchBox';

export default SearchBox;
